import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import { CHIP_TONES } from "../QuestionList/FilterChips";
import { focusRing } from "../../pages/Exams/TestBuilder/ui";

// Shared look of the variant pages (the tokens of the list pages and the test builder). Phones get 44px buttons.
// A button waiting for a write is aria-disabled, not disabled: it keeps the keyboard focus (its click does nothing).
const buttonBase = `inline-flex h-10 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-4 text-sm font-medium transition duration-200 disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 max-sm:h-11 ${focusRing}`;
export const primaryButtonClass = `${buttonBase} bg-blue-600 text-white shadow-sm shadow-blue-600/25 enabled:hover:bg-blue-700`;
export const secondaryButtonClass = `${buttonBase} bg-white text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 enabled:hover:bg-slate-50 enabled:hover:text-slate-900`;
// The variant header's actions, laid out as FolderBar's and the BTS pages' (OverflowMenu's breakpoint, md): 44px tall
// on phones, where the one in sight takes the row's width next to "⋯"; 36px from md up. The quiet one is framed on
// phones and only icon and text from md up. An aria-disabled one (a write runs, or Confirm while the check blocks it)
// keeps the focus: it is dimmed by its colours, not by its opacity (which would fade its focus ring too), and does
// not light up on hover.
const headerButtonBase = `inline-flex h-11 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg text-sm font-medium transition duration-200 disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed max-md:flex-1 md:h-9 ${focusRing}`;
export const headerPrimaryClass = `${headerButtonBase} bg-blue-600 px-4 text-white shadow-sm shadow-blue-600/25 enabled:hover:bg-blue-700 aria-disabled:bg-blue-300 aria-disabled:shadow-none aria-disabled:hover:bg-blue-300`;
export const headerQuietClass = `${headerButtonBase} bg-white px-3 text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 enabled:hover:bg-slate-50 aria-disabled:text-slate-400 aria-disabled:hover:bg-white md:bg-transparent md:text-slate-600 md:shadow-none md:ring-0 md:enabled:hover:bg-slate-100 md:enabled:hover:text-slate-900 md:aria-disabled:hover:bg-transparent md:aria-disabled:hover:text-slate-400`;
// The small buttons of a place in the composition: quiet (icon and text, no frame), "Add" in the links' blue; its row
// starts by its padding to the left (slotActionsClass), so the icons line up with the text above.
const slotButtonBase = `inline-flex h-8 shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-lg px-2 text-xs font-medium transition duration-200 disabled:cursor-not-allowed disabled:opacity-50 max-sm:h-11 max-sm:px-3 max-sm:text-sm ${focusRing}`;
export const slotButtonClass = `${slotButtonBase} text-slate-600 enabled:hover:bg-slate-100 enabled:hover:text-slate-900`;
export const slotAddButtonClass = `${slotButtonBase} text-blue-700 enabled:hover:bg-blue-50 enabled:hover:text-blue-800`;
export const slotRemoveButtonClass = `${slotButtonBase} text-slate-500 enabled:hover:bg-red-50 enabled:hover:text-red-700`;
export const slotActionsClass = "-ml-2 flex flex-wrap items-center gap-1 max-sm:-ml-3";
export const fieldClass =
  "block w-full rounded-lg border-slate-300 px-3 text-sm text-slate-900 shadow-sm transition duration-200 placeholder:text-slate-400 hover:border-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15";
export const dialogFooterClass =
  "-mx-6 -mb-6 mt-6 flex flex-col-reverse gap-2 rounded-b-2xl border-t border-slate-200 bg-slate-50/60 px-6 py-4 sm:flex-row sm:justify-end";
export const badgeClass = "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset";

export const VARIANT_STATUSES = ["draft", "confirmed"];
const STATUS_TONES = { draft: CHIP_TONES.amber, confirmed: CHIP_TONES.green };
export const statusTone = (status) => STATUS_TONES[status] || CHIP_TONES.slate;

// Draft (amber) or confirmed (green).
export function VariantStatusBadge({ status, className = "" }) {
  const [t] = useTranslation();
  return (
    <span className={`${badgeClass} ${statusTone(status).badge} ${className}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${statusTone(status).dot}`} aria-hidden="true" />
      {t(`variants.status.${status}`, { defaultValue: status || "" })}
    </span>
  );
}

// In an active booklet: read only. Quiet grey with its lock (the status badge beside it keeps the colour).
export function LockedBadge() {
  const [t] = useTranslation();
  return (
    <span className={`${badgeClass} ${CHIP_TONES.slate.badge}`} title={t("variants.locked_title")}>
      <LockClosedIcon className="h-3.5 w-3.5" aria-hidden="true" />
      {t("variants.locked")}
    </span>
  );
}

const BOOKLET_TONES = { draft: CHIP_TONES.slate, active: CHIP_TONES.blue, archived: CHIP_TONES.slate };

// The booklets a variant is in: name and status. `linked`: each opens its booklet's page (/bts is for admins).
// `limit` (the list's rows): at most that many chips, the active booklets (they lock the variant) first; the rest are
// counted in the last one ("+4", their names in its tooltip and for screen readers).
export function BookletChips({ booklets, className = "", linked = false, limit = 0 }) {
  const [t] = useTranslation();
  if (!booklets?.length) return null;
  const sorted = limit ? [...booklets].sort((a, b) => (b.status === "active") - (a.status === "active")) : booklets;
  const shown = limit && sorted.length > limit ? sorted.slice(0, limit - 1) : sorted;
  const rest = sorted.slice(shown.length);
  const restNames = rest.map((booklet) => booklet.name).join(", ");
  return (
    <ul className={`flex flex-wrap gap-1.5 ${className}`} aria-label={t("variants.booklets")}>
      {shown.map((booklet) => {
        const chip = `${badgeClass} max-w-full ${(BOOKLET_TONES[booklet.status] || CHIP_TONES.slate).badge}`;
        const content = (
          <>
            {booklet.status === "active" && <LockClosedIcon className="h-3 w-3 shrink-0" aria-hidden="true" />}
            <span className="min-w-0 truncate">{booklet.name}</span>
            <span className="font-normal opacity-75">· {t(`variants.booklet_status.${booklet.status}`, { defaultValue: booklet.status })}</span>
          </>
        );
        return linked && booklet.uuid ? (
          <li key={booklet.uuid} className="max-w-full">
            <Link
              to={`/bts/${booklet.uuid}`}
              title={booklet.name}
              className={`${chip} underline-offset-2 transition duration-200 hover:underline max-sm:min-h-11 ${focusRing}`}
            >
              {content}
            </Link>
          </li>
        ) : (
          <li key={booklet.uuid} className={chip} title={booklet.name}>
            {content}
          </li>
        );
      })}
      {rest.length > 0 && (
        <li className={`${badgeClass} ${CHIP_TONES.slate.badge}`} title={restNames}>
          <span aria-hidden="true">+{rest.length}</span>
          <span className="sr-only">{t("variants.more_booklets", { count: rest.length, names: restNames })}</span>
        </li>
      )}
    </ul>
  );
}

// "QA Moderator", or the username of an account without a name.
export const accountName = (account) =>
  [account?.first_name, account?.last_name].filter(Boolean).join(" ").trim() || account?.username || "";

// A question's text as one plain line for a short preview: no tags, entities decoded, math delimiters dropped.
let decoder = null;
export function plainText(html) {
  if (!html) return "";
  const text = String(html)
    .replace(/<(br|\/p|\/div|\/li|\/tr)\b[^>]*>/gi, " ")
    .replace(/<[^>]*>/g, "");
  decoder = decoder || document.createElement("textarea");
  decoder.innerHTML = text;
  return decoder.value
    .replace(/\\[()[\]]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export const hasImage = (html) => /<img\b/i.test(html || "");

// A question's HTML flattened for a one- or two-line preview with its formulas: no pictures, no blocks (paragraphs,
// tables, lists become spaces), display math made inline.
export const previewHtml = (html) =>
  String(html || "")
    .replace(/<img\b[^>]*>/gi, " ")
    .replace(/<\/?(p|div|br|li|ul|ol|table|thead|tbody|tr|td|th|h[1-6]|blockquote|pre)\b[^>]*>/gi, " ")
    .replace(/\\\[/g, "\\(")
    .replace(/\\\]/g, "\\)")
    .replace(/\$\$/g, "$")
    .replace(/\s+/g, " ")
    .trim();
